import React, { useEffect } from 'react';
import { X, Shield, FileText } from 'lucide-react';
type LegalDocType = 'privacy' | 'terms' | null;
interface LegalModalProps {
  isOpen: boolean;
  type: LegalDocType;
  onClose: () => void;
}
export function LegalModal({
  isOpen,
  type,
  onClose
}: LegalModalProps) {
  if (!isOpen || !type) return null;
  // Prevent scrolling on body when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const content = {
    privacy: {
      title: 'Privacy Policy',
      icon: Shield,
      body: <>
          <p data-id="element-35">
            <strong data-id="element-36">Last Updated: {new Date().toLocaleDateString()}</strong>
          </p>

          <h4 data-id="element-37">1. Introduction</h4>
          <p data-id="element-38">
            Welcome to the Feline Harmony Blueprint. We respect your privacy and
            are committed to protecting your personal data. This privacy policy
            will inform you as to how we look after your personal data when you
            visit our website and tell you about your privacy rights and how the
            law protects you.
          </p>

          <h4 data-id="element-39">2. Information We Collect</h4>
          <p data-id="element-40">
            We may collect, use, store and transfer different kinds of personal
            data about you which we have grouped together follows:
          </p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-41">
            <li data-id="element-42">
              <strong data-id="element-43">Identity Data:</strong> includes first name, last name.
            </li>
            <li data-id="element-44">
              <strong data-id="element-45">Contact Data:</strong> includes email address.
            </li>
            <li data-id="element-46">
              <strong data-id="element-47">Transaction Data:</strong> includes details about payments
              to and from you and other details of products you have purchased
              from us.
            </li>
          </ul>

          <h4 data-id="element-48">3. How We Use Your Data</h4>
          <p data-id="element-49">
            We will only use your personal data when the law allows us to. Most
            commonly, we will use your personal data in the following
            circumstances:
          </p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-50">
            <li data-id="element-51">To process and deliver your order (digital download).</li>
            <li data-id="element-52">
              To manage our relationship with you (notify you about changes to
              our terms or privacy policy).
            </li>
            <li data-id="element-53">To send you the Feline Harmony Blueprint materials.</li>
          </ul>

          <h4 data-id="element-54">4. Data Security</h4>
          <p data-id="element-55">
            We have put in place appropriate security measures to prevent your
            personal data from being accidentally lost, used or accessed in an
            unauthorized way, altered or disclosed.
          </p>

          <h4 data-id="element-56">5. Third-Party Links</h4>
          <p data-id="element-57">
            This website may include links to third-party websites, plug-ins and
            applications. Clicking on those links or enabling those connections
            may allow third parties to collect or share data about you. We do
            not control these third-party websites and are not responsible for
            their privacy statements.
          </p>
        </>
    },
    terms: {
      title: 'Terms of Service',
      icon: FileText,
      body: <>
          <p data-id="element-58">
            <strong data-id="element-59">Last Updated: {new Date().toLocaleDateString()}</strong>
          </p>

          <h4 data-id="element-60">1. Agreement to Terms</h4>
          <p data-id="element-61">
            By accessing or using the Feline Harmony Blueprint, you agree to be
            bound by these Terms of Service. If you disagree with any part of
            the terms, then you may not access the service.
          </p>

          <h4 data-id="element-62">2. Intellectual Property</h4>
          <p data-id="element-63">
            The Service and its original content (including the Feline Harmony
            Blueprint ebook, videos, and guides), features, and functionality
            are and will remain the exclusive property of Feline Harmony
            Blueprint and its licensors. The content is protected by copyright,
            trademark, and other laws. Our trademarks and trade dress may not be
            used in connection with any product or service without the prior
            written consent of Feline Harmony Blueprint.
          </p>

          <h4 data-id="element-64">3. Digital Products & Refunds</h4>
          <p data-id="element-65">
            We offer a 7-day money-back guarantee on the Feline Harmony
            Blueprint. If you are not satisfied with the results after applying
            the methods for 7 days, you may request a full refund by contacting
            our support team.
          </p>

          <h4 data-id="element-66">4. Disclaimer</h4>
          <p data-id="element-67">
            The advice provided in the Feline Harmony Blueprint is for
            educational purposes only. While our methods are based on feline
            behavioral science, every cat is unique. We are not responsible for
            any damage or injury that may occur while implementing these
            techniques. Always consult with a veterinarian for medical issues.
          </p>

          <h4 data-id="element-68">5. Limitation of Liability</h4>
          <p data-id="element-69">
            In no event shall Feline Harmony Blueprint, nor its directors,
            employees, partners, agents, suppliers, or affiliates, be liable for
            any indirect, incidental, special, consequential or punitive
            damages, including without limitation, loss of profits, data, use,
            goodwill, or other intangible losses, resulting from your access to
            or use of or inability to access or use the Service.
          </p>
        </>
    }
  };
  const activeContent = content[type];
  const Icon = activeContent.icon;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-70">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-71" />

      {/* Modal Container */}
      <div className="relative bg-[#0f0f0f] border border-gray-800 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200" data-id="element-72">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800 shrink-0" data-id="element-73">
          <div className="flex items-center gap-3" data-id="element-74">
            <div className="p-2 bg-orange-900/20 rounded-lg" data-id="element-75">
              <Icon className="w-5 h-5 text-orange-500" data-id="element-76" />
            </div>
            <h3 className="text-xl font-bold text-white" data-id="element-77">
              {activeContent.title}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-gray-800 rounded-md" data-id="element-78">
            <X className="w-6 h-6" data-id="element-79" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6 space-y-4 text-gray-300 leading-relaxed text-sm md:text-base custom-scrollbar" data-id="element-80">
          <div className="prose prose-invert prose-orange max-w-none prose-headings:text-white prose-headings:font-bold prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-2 prose-p:mb-4 prose-ul:mb-4" data-id="element-81">
            {activeContent.body}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800 bg-[#0a0a0a] rounded-b-xl shrink-0 flex justify-end" data-id="element-82">
          <button onClick={onClose} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-md text-sm font-medium transition-colors" data-id="element-83">
            Close
          </button>
        </div>
      </div>
    </div>;
}